/* AuditPolicyVersionsViewRenderer.js */

"use strict";
const _ = require("lodash");
const formatters = require("./cellFormaters");
const CommitConfigurationComponent = require("./commit_config_veil/CommitConfigurationComponent");
const Registrations = require("../../../../../RegistrationNamespaces");

/* global Slick*/
/**
 * @param {AuditPolicyVersionsViewComponent} model
 */
class AuditPolicyVersionsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("versions-view-gl");
        this._rootElem.classList.add("versions-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._gridSection = document.createElement("div");
        this._emptyContainer = document.createElement("div");

        this._columns = [
            {
                id: "date",
                width: 200,
                name: "Date",
                field: "date",
                toolTip: "Date",
                sortable: true,
                resizable: true,
            },
            {
                id: "commitHash",
                width: 470,
                name: "Commit Hash",
                field: "commitHash",
                toolTip: "Commit Hash",
                sortable: true,
                resizable: true,
            },
            {
                id: "author",
                width: 340,
                name: "Author",
                field: "author",
                toolTip: "Author",
                sortable: true,
                resizable: true,
            },
            {
                id: "commitMessage",
                width: 610,
                name: "Commit Message",
                field: "commitMessage",
                toolTip: "Commit Message",
                cssClass: "comment-column",
                headerCssClass: "comment-column",
                sortable: true,
                resizable: true,
            },
            {
                id: "action_col",
                width: 310,
                name: "Actions",
                field: "actions",
                toolTip: "Actions",
                formatter: formatters.actionButtonsFormatter,
                cssClass: "action-column centered",
                headerCssClass: "action-column centered",
                sortable: false,
                resizable: false,
            },
        ];

        this._slickTableContainer = document.createElement("commits-table");
        this._slickTable = undefined;
        this._tableOptions = {
            editable: false,
            enableTextSelectionOnCells: true,
            enableCellNavigation: false,
            enableAsyncPostRender: false,
            forceFitColumns: true,
            autoHeight: false,
            autoEdit: false,
            resizable: true,
            rowHeight: 40,
            enableColumnReorder: false,
        };

        this._onRowCountChange = this._onRowCountChange.bind(this);
        this._slickSort = this._slickSort.bind(this);
        this._onWindowResized = _.debounce(this._onWindowResized.bind(this), 300);
        // initiate observer
        this._resObserver = this.observeResize();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on("UpdateView", (this._onUpdateView = this._onUpdateView.bind(this)));
        this._model.on("SizeChanged", (this._onSizeChanged = this._onSizeChanged.bind(this)));
        window.addEventListener("resize", this._onWindowResized);
    }

    _unregisterListeners() {
        this._model.removeListener("updateView", this._onUpdateView);
        this._model.removeListener("SizeChanged", this._onSizeChanged);
        window.removeEventListener("resize", this._onWindowResized);
    }

    _registerSlickListeners() {
        this._unregisterSlickListeners();
        if (this._slickTable) {
            this._slickDataView.onRowCountChanged.subscribe(this._onRowCountChange);
            this._slickTable.onClick.subscribe(
                (this._onSlickClick = this._onSlickClick.bind(this))
            );
            this._slickTable.onSort.subscribe(this._slickSort);
        }
    }

    _unregisterSlickListeners() {
        if (this._slickTable) {
            this._slickDataView.onRowCountChanged.unsubscribe(this._onRowCountChange);
            this._slickTable.onClick.unsubscribe(this._onSlickClick);
            this._slickTable.onSort.unsubscribe(this._slickSort);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._unregisterSlickListeners();
        this._resObserver.unobserve(this._rootElem);

        return Promise.resolve().then(
            () => {
                this._model = null;
                this._gridSection = null;
                this._emptyContainer = null;
                this._slickTable = null;
                this._slickTableContainer = null;
                this._slickDataView = null;
                this._slickDataItems = null;
                this._columns = null;
                this._tableOptions = null;

                this._rootElem.remove();
                this._rootElem = null;
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "AuditPolicyVersionsViewRenderer.shutdown() error."
                );
            }
        );
    }

    render() {
        this.rootElement.innerHTML = "";
        this.rootElement.appendChild(this._gridSection);
        this._renderGrid();
        this._renderEmptyGrid();

        return this.model.withLoadingVeil(this.model.whenModelIsReady()).then(() => {
            this._slickDataView = new Slick.Data.DataView();
            this._slickDataItems = this._generateSlickData();
            this._slickDataView.setItems(this._slickDataItems);
            this._slickTable = new Slick.Grid(
                this._slickTableContainer,
                this._slickDataView,
                this._columns,
                this._tableOptions
            );

            this._slickTable.setSelectionModel(new Slick.RowSelectionModel());
            this._slickTableContainer.appendChild(this._emptyContainer);
            this._toggleNoDataPlaceholder(_.isEmpty(this._slickDataItems));
            this._registerListeners();
            this._registerSlickListeners();
            this._resObserver.observe(this._rootElem);
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _generateSlickData() {
        return this._model._commitData.map((commit) => {
            return {
                id: commit.hash,
                date: this._dateToLocal(commit.timestamp),
                commitHash: commit.hash,
                author: commit.authorName,
                commitMessage: commit.commitMessage,
            };
        });
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _onUpdateView() {
        this._model.whenModelIsReady().then(() => {
            this._slickDataItems = this._generateSlickData();
            this._slickDataView.setItems(this._slickDataItems);
            this._slickTable.invalidateAllRows();
            this._slickTable.resizeCanvas();
            this._toggleNoDataPlaceholder(_.isEmpty(this._slickDataItems));
        });
    }
    _onRowCountChange(e, {current}) {
        // console.time("Row Count");
        this._slickTable.invalidateAllRows();
        this._slickTable.resizeCanvas();
        this._toggleNoDataPlaceholder(!current);
        // console.timeEnd("Row Count");
    }
    _onSizeChanged() {
        this._onWindowResized();
    }

    _toggleNoDataPlaceholder(state) {
        this._slickTableContainer.classList.toggle("empty", state);
    }

    _slickSort(e, args) {
        // console.time("Sort");
        // console.time("Sort logic");
        this._slickDataView.sort((a, b) => {
            let x = a[args.sortCol.field],
                y = b[args.sortCol.field];
            if (x === null || x === undefined) {
                x = args.sortCol.type === "number" ? 0 : "";
            }
            if (y === null || y === undefined) {
                y = args.sortCol.type === "number" ? 0 : "";
            }
            if (typeof x === "number" && typeof y === "number") {
                return x - y;
            } else {
                if (typeof x === "string") {
                    x = x.toLowerCase();
                }
                if (typeof y === "string") {
                    y = y.toLowerCase();
                }
                return x === y ? 0 : x > y ? 1 : -1;
            }
        }, args.sortAsc);
        this._slickTable.invalidate();
        this._slickTable.render();
    }
    _onSlickClick(e, args) {
        const {cell, row, grid} = args;
        const cellid = grid.getColumns()[cell].id;
        if (cellid === "action_col") {
            const _type = e.target.getAttribute("action");
            const rowData = grid.getDataItem(row);
            const commitHash = rowData.commitHash;
            switch (_type) {
                case "view":
                    this._launchCommitInfo(commitHash);
                    break;
                case "compare":
                    this._launchCommitCompare(commitHash);
                    break;
                case "apply":
                    this._launchAuditPolicyEditorTab(rowData);
                    break;
            }
        }
        const cellNode = grid.getCellNode(row, 1);
        cellNode.closest(".grid-canvas").find(".slick-row").removeClass("selected");
        cellNode.closest(".slick-row").addClass("selected");
    }

    _onWindowResized() {
        this._slickTable.resizeCanvas();
    }

    _launchCommitInfo(commit) {
        const proms = [];
        proms.push(
            this._model._getCommitFileContent(commit).catch((err) => {
                this._logger.warn(
                    {error: err, hash: commit},
                    "AuditPolicyVersionsViewRenderer: could not retrieve committed policy file for commit details."
                );
                return undefined;
            })
        );
        proms.push(this._model._modalUserFeedback.hideDisplayed());
        Promise.all(proms).then((data) => {
            const commitConfigComp = new CommitConfigurationComponent({
                data: data[0],
            });
            this._model._modalUserFeedback.displayCustom(commitConfigComp, {
                title: "Audit Policy Commit Details",
            });
        });
    }

    async _launchCommitCompare(commit) {
        const compareVals = [];

        //build compareVals
        for (const indvCommit of this._model._commitData) {
            const data = await this._model._getCommitFileContent(indvCommit.hash).catch((err) => {
                this._logger.warn(
                    {error: err, hash: indvCommit.hash},
                    "AuditPolicyVersionsViewRenderer: could not retrieve committed policy file for compare; skipping commit."
                );
                return undefined;
            });
            compareVals.push({commitHash: indvCommit.hash, data: data});
        }

        const policy = await this._model._gameEditor._ConfigAuditPolicyEntity.exportPolicy(
            [this._model._gameEditor._auditPolicy.pristineProperties],
            this._model._gameEditor._activeOrgId
        );
        const parsedPolicy = JSON.parse(policy)[0];

        const backLink = [
            {
                value: {
                    fqn: Registrations.AUDIT_POLICY_EDITOR_VIEW_VERSIONS,
                    viewState: {
                        configExecutionEntity: {},
                    },
                },
                content: "Audit Policy Versions History",
                left: {
                    arrow: {
                        direction: "left",
                    },
                },
            },
        ];

        //send compareVals and commit to compare view
        this._model.switchSubView(
            Registrations.AUDIT_POLICY_EDITOR_SUBVIEW_COMPARE,
            {
                compareVals: compareVals,
                activeCommit: commit,
                policy: parsedPolicy,
            },
            backLink
        );
    }

    _launchAuditPolicyEditorTab(commitInfo) {
        this._model._modalUserFeedback.hideDisplayed().then(() => {
            const msg = `<div class="commit-apply-message">
            <p>You are about to apply the following Audit Policy configuration:</p>
            <ul>
            <li>Commit Hash: ${commitInfo.commitHash} </li>
            <li>Author: ${commitInfo.author}  </li>
            <li>Date: ${commitInfo.date} </li>
            <li>Commit Message: "${commitInfo.commitMessage}"</li>
            </ul>
            <p>This will redirect you to the Config Audit Details view with the selected commit's data applied.</p>
            <p>Please review all properties prior to saving as some references may have been removed or renamed.</p>
            <p>When ready, you must save the form to apply the changes.</p></div>`;
            this._model._modalUserFeedback
                .displayConfirmation(msg, {
                    title: "Apply Version",
                    confirmDetails: {
                        text: "Apply",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Cancel",
                        type: "text",
                    },
                    enableHtml: true,
                })
                .then(() => {
                    this._model.switchView(Registrations.AUDIT_POLICY_EDITOR_VIEW_DETAILS, {
                        commitHash: commitInfo.commitHash,
                    });
                })
                .catch(() => {
                    return Promise.resolve();
                });
        });
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _renderGrid() {
        this._gridSection.classList.add("grid-section");
        this._gridSection.innerHTML = "";
        this._slickTableContainer.appendChild(this._emptyContainer);
        this._gridSection.appendChild(this._slickTableContainer);
    }

    _renderEmptyGrid() {
        this._emptyContainer.innerHTML = "";
        this._emptyContainer.classList.add("no-data-placeholder");
        this._emptyContainer.innerHTML = "No commits found";
    }

    observeResize() {
        const resObs = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.contentRect) {
                    this._model.emit("SizeChanged");
                }
            });
        });
        return resObs;
    }
    _dateToLocal(timeStamp) {
        if (!timeStamp) {
            return "";
        }
        if (typeof timeStamp === "string") {
            timeStamp = parseInt(timeStamp);
        } else {
            return "";
        }
        const date = new Date(timeStamp * 1000);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = AuditPolicyVersionsViewRenderer;
